import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Card, Spin, Typography } from 'antd'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentMethodMessagingElement } from '@stripe/react-stripe-js'
import type { FinancingApplicationSummary } from '@serviceflow/shared'
import { env } from '../../config/environment'

const { Text, Title } = Typography
const API_URL = env.apiBaseUrl.replace(/\/api$/, '')

export interface AffirmFinancingBlockProps {
  amountCents: number
  estimateId?: string
  invoiceId?: string
  token?: string
  pdfToken?: string
  source?: string
  compact?: boolean
}

interface EligibilityResponse {
  affirmAvailable: boolean
  publishableKey: string | null
  amountCents: number
  currency: string
  application: FinancingApplicationSummary | null
}

function buildEligibilityQuery(props: AffirmFinancingBlockProps): string {
  const params = new URLSearchParams()
  if (props.estimateId) params.set('estimateId', props.estimateId)
  if (props.invoiceId) params.set('invoiceId', props.invoiceId)
  if (props.token) params.set('token', props.token)
  if (props.pdfToken) params.set('pdfToken', props.pdfToken)
  return params.toString()
}

function applicationStatusLabel(application: FinancingApplicationSummary | null): string | null {
  if (!application) return null
  switch (application.status) {
    case 'preapproved':
      return application.approvedAmountCents
        ? application.expiresAt
          ? `Pre-approved for $${(application.approvedAmountCents / 100).toLocaleString()}`
          : `Affirm approved for $${(application.approvedAmountCents / 100).toLocaleString()} — confirming payment`
        : 'Pre-approved'
    case 'declined':
      return 'Financing not approved'
    case 'expired':
      return 'Pre-approval expired — check again'
    case 'confirmed':
      return 'Financing confirmed'
    case 'pending':
      return 'Application in progress'
    default:
      return null
  }
}

export const AffirmFinancingBlock: React.FC<AffirmFinancingBlockProps> = (props) => {
  const [loading, setLoading] = useState(true)
  const [sessionLoading, setSessionLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [eligibility, setEligibility] = useState<EligibilityResponse | null>(null)

  const loadEligibility = useCallback(async () => {
    if (props.amountCents <= 0) {
      setEligibility(null)
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      setError(null)
      const query = buildEligibilityQuery(props)
      const response = await fetch(`${API_URL}/api/financing/eligibility?${query}`)
      const result = await response.json()
      if (!result.success) {
        setEligibility(null)
        setError(result.error || 'Unable to load financing options')
        return
      }
      setEligibility(result.data as EligibilityResponse)
    } catch {
      setError('Unable to load financing options')
      setEligibility(null)
    } finally {
      setLoading(false)
    }
  }, [props.amountCents, props.estimateId, props.invoiceId, props.token, props.pdfToken])

  useEffect(() => {
    void loadEligibility()
  }, [loadEligibility])

  const stripePromise = useMemo(() => {
    if (!eligibility?.publishableKey) return null
    return loadStripe(eligibility.publishableKey)
  }, [eligibility?.publishableKey])

  const handleCheckFinancing = async () => {
    try {
      setSessionLoading(true)
      setError(null)
      const response = await fetch(`${API_URL}/api/financing/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          estimateId: props.estimateId,
          invoiceId: props.invoiceId,
          token: props.token,
          pdfToken: props.pdfToken,
          amountCents: props.amountCents,
          source: props.source,
        }),
      })
      const result = await response.json()
      if (!result.success) {
        setError(result.error || 'Unable to start financing application')
        return
      }
      await loadEligibility()
    } catch {
      setError('Unable to start financing application')
    } finally {
      setSessionLoading(false)
    }
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: props.compact ? 8 : 16 }}>
        <Spin size="small" />
      </div>
    )
  }

  if (!eligibility?.affirmAvailable || !stripePromise) {
    return null
  }

  const statusLabel = applicationStatusLabel(eligibility.application)

  return (
    <Card size={props.compact ? 'small' : 'default'} style={{ marginTop: props.compact ? 8 : 16 }}>
      <Title level={5} style={{ marginTop: 0 }}>
        Pay over time with Affirm
      </Title>
      <Text type="secondary">
        Check your financing options before you commit. Applying is a soft credit check and won&apos;t affect your score.
      </Text>

      {statusLabel && (
        <Alert
          style={{ marginTop: 12 }}
          type={
            eligibility.application?.status === 'declined'
              ? 'warning'
              : eligibility.application?.status === 'preapproved'
                ? 'success'
                : 'info'
          }
          message={statusLabel}
          showIcon
        />
      )}

      <div style={{ marginTop: 16 }}>
        <Elements stripe={stripePromise}>
          <PaymentMethodMessagingElement
            options={{
              amount: eligibility.amountCents,
              currency: eligibility.currency.toUpperCase(),
              paymentMethodTypes: ['affirm'],
            }}
          />
        </Elements>
      </div>

      {error && (
        <Alert style={{ marginTop: 12 }} type="error" message={error} showIcon />
      )}

      <Button
        type="default"
        block
        style={{ marginTop: 16 }}
        loading={sessionLoading}
        onClick={() => void handleCheckFinancing()}
      >
        Check your financing options
      </Button>
    </Card>
  )
}

export default AffirmFinancingBlock
